/* eslint-disable i18next/no-literal-string */
import type { AssetMedia } from '@aikoa/common'
import { useMutation } from '@tanstack/react-query'
import { decode } from 'base64-arraybuffer'
import {
  cacheDirectory,
  downloadAsync,
  EncodingType,
  readAsStringAsync,
} from 'expo-file-system/legacy'
import { useCallback } from 'react'
import { Platform } from 'react-native'
import { useAlert } from '~/components/GlobalAlert.tsx'
import { supabase } from '~/lib/supabase'
import { trpc } from '~/lib/trpc.tsx'
import { normalizeWebImageUpload } from './normalizeWebImageUpload'
import {
  generateFileName,
  getErrorMessage,
  getFileType,
  validateFileSize,
  validateFileType,
} from './utils'

interface UseFileUploadOptions {
  bucket: string
  maxSizeBytes: number
  accept: string[]
  order: number
  onUpload?: (media: AssetMedia) => void
  onError?: (error: string) => void
}

export function useFileUpload({
  bucket,
  maxSizeBytes,
  accept,
  order,
  onUpload,
  onError,
}: UseFileUploadOptions) {
  const { showAlert } = useAlert()
  const maxSizeMB = Math.round(maxSizeBytes / 1024 / 1024)
  const acceptsImages = accept.some((acceptedType) => acceptedType.startsWith('image/'))

  const handleError = useCallback(
    (error: string) => {
      const message = getErrorMessage(error, maxSizeMB)
      onError?.(message)

      showAlert({
        type: 'error',
        title: 'Tiedoston lataus epäonnistui',
        description: message,
      })
    },
    [maxSizeMB, onError, showAlert]
  )

  const uploadMutation = useMutation({
    mutationFn: async ({
      fileData,
      fileName,
      mimeType,
    }: {
      fileData: ArrayBuffer
      fileName: string
      mimeType?: string
    }): Promise<AssetMedia> => {
      const filePath = `${Date.now()}/${fileName}`

      const { data, error } = await supabase.storage.from(bucket).upload(filePath, fileData, {
        contentType: mimeType || 'image/jpeg',
        upsert: false,
      })

      if (error) {
        throw new Error(error.message)
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from(bucket).getPublicUrl(data.path)

      return {
        url: publicUrl,
        type: getFileType(mimeType),
        description: '',
        order,
        storageId: data.id,
        storagePath: data.fullPath,
      }
    },
    onSuccess: (media) => {
      onUpload?.(media)
    },
    onError: (error) => {
      handleError(error instanceof Error ? error.message : 'Upload failed')
    },
  })

  const uploadImageUrlMutation = useMutation(trpc.supabase.uploadImageFromUrl.mutationOptions())

  const processFile = useCallback(
    async (file: File | { uri: string; name?: string; type?: string }) => {
      try {
        let fileData: ArrayBuffer
        let fileName: string
        let mimeType: string | undefined

        if (Platform.OS === 'web' && file instanceof File) {
          if (!validateFileType(file.type, accept)) {
            throw new Error('File type not supported')
          }
          if (!validateFileSize(file.size, maxSizeBytes)) {
            throw new Error('File too large')
          }

          fileData = await file.arrayBuffer()
          fileName = generateFileName(file.name)
          mimeType = file.type

          if (acceptsImages) {
            const normalizedImageUpload = await normalizeWebImageUpload({
              fileData,
              fileName,
              mimeType,
            })

            fileData = normalizedImageUpload.fileData
            fileName = normalizedImageUpload.fileName
            mimeType = normalizedImageUpload.mimeType
          }
        } else if ('uri' in file) {
          const base64 = await readAsStringAsync(file.uri, {
            encoding: EncodingType.Base64,
          })
          fileData = decode(base64)
          fileName = generateFileName(file.name)
          mimeType = file.type
        } else {
          throw new Error('Invalid file format')
        }

        uploadMutation.mutate({ fileData, fileName, mimeType })
      } catch (error) {
        handleError(error instanceof Error ? error.message : 'Processing failed')
      }
    },
    [accept, acceptsImages, maxSizeBytes, uploadMutation, handleError]
  )

  const processUrl = useCallback(
    async (url: string) => {
      try {
        let validUrl: URL
        try {
          validUrl = new URL(url)
          if (!['http:', 'https:'].includes(validUrl.protocol)) {
            throw new Error('Invalid URL protocol')
          }
        } catch {
          throw new Error('Invalid URL format')
        }

        if (acceptsImages) {
          const uploadedMedia = await uploadImageUrlMutation.mutateAsync({
            url: validUrl.toString(),
            bucket: bucket as 'avatars' | 'property-media',
            order,
            maxSizeBytes,
          })

          onUpload?.(uploadedMedia)
          return
        }

        const response = await fetch(url)
        if (!response.ok) {
          throw new Error('Network error')
        }

        const contentType = response.headers.get('content-type')
        const contentLength = response.headers.get('content-length')

        if (contentLength && Number.parseInt(contentLength) > maxSizeBytes) {
          throw new Error('File too large')
        }

        if (!validateFileType(contentType, accept)) {
          throw new Error('File type not supported')
        }

        let fileData: ArrayBuffer
        let fileName: string
        let mimeType: string | undefined
        const urlPath = validUrl.pathname
        const originalName = urlPath.split('/').pop() || 'download'
        fileName = generateFileName(originalName)

        if (Platform.OS === 'web') {
          fileData = await response.arrayBuffer()
          mimeType = contentType ?? undefined
        } else {
          const downloadResult = await downloadAsync(url, `${cacheDirectory}${fileName}`)

          if (downloadResult.status !== 200) {
            throw new Error('Network error')
          }

          const base64 = await readAsStringAsync(downloadResult.uri, {
            encoding: EncodingType.Base64,
          })
          fileData = decode(base64)
        }

        uploadMutation.mutate({
          fileData,
          fileName,
          mimeType,
        })
      } catch (error) {
        handleError(error instanceof Error ? error.message : 'URL processing failed')
      }
    },
    [
      accept,
      acceptsImages,
      bucket,
      maxSizeBytes,
      onUpload,
      order,
      uploadImageUrlMutation,
      uploadMutation,
      handleError,
    ]
  )

  return {
    processFile,
    processUrl,
    isUploading: uploadMutation.isPending || uploadImageUrlMutation.isPending,
  }
}
